'use client';

import 'components/elements/map/leaflet-fix.css';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Shippori_Mincho_B1 } from 'next/font/google';
import { useEffect, useMemo, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';

import type { Location } from 'infra/GetLocations';
import { formatJpDateTime } from 'lib/format';

type Props = { locations: Location[] };

const shippori = Shippori_Mincho_B1({
  weight: ['400'],
  subsets: ['latin'],
  display: 'swap',
});

export default function MapView({ locations }: Props) {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // LeafletアイコンURLのビルド対策（Nextでよく起きるやつ）
  useEffect(() => {
    if (isClient) {
      // @ts-expect-error - _getIconUrl is not in types but exists at runtime
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl:
          'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
        shadowUrl:
          'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
      });
    }
  }, [isClient]);

  // 初期センターとbounds（ピンが無いときは東京駅）
  const { center, bounds } = useMemo(() => {
    if (locations.length === 0) {
      return {
        center: [35.681236, 139.767125] as [number, number],
        bounds: null,
      };
    }

    if (locations.length === 1) {
      return {
        center: [locations[0].lat as number, locations[0].lng as number] as [
          number,
          number,
        ],
        bounds: null,
      };
    }

    // 複数の場合はboundsを計算
    const latLngs = locations.map((loc) => [
      loc.lat as number,
      loc.lng as number,
    ]);
    const bounds = L.latLngBounds(latLngs as [number, number][]);

    return {
      center: bounds.getCenter(),
      bounds: bounds,
    };
  }, [locations]);

  if (!isClient) {
    return (
      <div
        style={{ height: '70vh', width: '100%' }}
        className='flex justify-center items-center bg-gray-100'
      >
        <div>マップを読み込み中...</div>
      </div>
    );
  }

  return (
    <div className='w-full h-full'>
      <style>
        {`
          .leaflet-control-attribution {
            display: none !important;
          }
        `}
      </style>
      <div
        className={`${shippori.className} flex flex-col gap-6 justify-center items-center pt-6 pb-16 text-center xs:py-5 xs:gap-4 px-5`}
      >
        <h1 className='text-2xl font-bold'>GAME Map</h1>
        <p className='mt-8 text-sm leading-relaxed'>
          試合が開催される場所を表示しています。
          <br />
          ピンをクリックしてください。
        </p>
      </div>

      <div style={{ height: '70vh', width: '100%' }}>
        <MapContainer
          key='map-container'
          center={center}
          zoom={bounds ? undefined : 12}
          bounds={bounds || undefined}
          boundsOptions={{ padding: [20, 20] }}
          scrollWheelZoom
          className='w-full h-full'
        >
          <TileLayer
            attribution='&copy; OpenStreetMap contributors'
            url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
          />

          {locations.map((loc) => {
            const list = Array.isArray(loc.matches)
              ? loc.matches.filter(Boolean)
              : [];
            const count = list.length;

            return (
              <Marker
                key={loc.id}
                position={[loc.lat as number, loc.lng as number]}
              >
                <Popup>
                  <div className='text-sm leading-snug'>
                    <div className='font-semibold'>{loc.title}</div>
                    {loc.address && <div className='mt-1'>{loc.address}</div>}

                    {/* 試合が0件 */}
                    {count === 0 && (
                      <div className='mt-2 text-muted-foreground'>
                        試合情報はまだありません
                      </div>
                    )}

                    {/* 試合が1件 */}
                    {count === 1 && (
                      <div className='mt-2'>
                        <div>
                          <strong>試合日:</strong>{' '}
                          {formatJpDateTime(list[0].matchDate)}
                        </div>
                        {list[0].url && (
                          <a
                            className='underline inline-block mt-0.5'
                            href={list[0].url}
                            target='_blank'
                            rel='noreferrer noopener'
                          >
                            詳細ページへ
                          </a>
                        )}
                      </div>
                    )}

                    {/* 試合が2件以上 */}
                    {count >= 2 && (
                      <ul className='mt-2 space-y-1'>
                        {list.map((m, i) => (
                          <li
                            key={i}
                            className='pt-1 border-t first:border-t-0 first:pt-0'
                          >
                            <div>
                              <strong>試合日:</strong>{' '}
                              {formatJpDateTime(m.matchDate)}
                            </div>
                            {m.url && (
                              <a
                                className='underline inline-block mt-0.5'
                                href={m.url}
                                target='_blank'
                                rel='noreferrer noopener'
                              >
                                詳細ページへ
                              </a>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
}
